import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../../../shared/Settings/appSettings';
import { useTBContext } from '../../Contexts/useTBStates';
import { normalizeEventRounds } from '../../../shared/validations/validateEvent';
import type {
    CourseTeeSetData,
    EventScoreWithId,
    TbEventWithId,
    TbRegistration,
    ViewEventData,
} from '@moment-golf/types';

/**
 * Shared Command Center data hook.
 * Owns event-list polling and event selection (details, registrations, teesets, scores).
 * Panel-specific state (score grids, tee navigation, etc.) lives in each panel.
 */
export function useCommandCenterData() {
    const auth = useAuth();
    const api = CreateApi();

    const {
        eventData, setEventData,
        setViewEvent,
        setViewEventData,
        setRoundValue,
        originalViewEvent,
        originalRegistrations,
        originalViewEventData,
    } = useTBContext();

    const [queryEventData, setQueryEventData] = useState(true);
    const eventDataRef = useRef(eventData);

    useEffect(() => {
        eventDataRef.current = eventData;
    }, [eventData]);

    const fetchEventData = useCallback(async () => {
        await api.get('/tb/GetAllUserEvents', {
            params: {
                userID: auth.user?.profile.sub,
                referenceDate: eventDataRef.current.length > 0
                    ? new Date(Math.max(...eventDataRef.current.map((event: TbEventWithId) => new Date(event.modifiedAt).getTime())))
                    : null,
            },
        })
            .then((res: { data: TbEventWithId[] }) => {
                if (eventDataRef.current.length === 0) {
                    setEventData(res.data);
                } else {
                    const updatedEventData = [...eventDataRef.current];
                    res.data.forEach((newEvent: TbEventWithId) => {
                        const existingEventIndex = updatedEventData.findIndex((event) => event._id === newEvent._id);
                        if (existingEventIndex !== -1) {
                            updatedEventData[existingEventIndex] = { ...updatedEventData[existingEventIndex], ...newEvent };
                        } else {
                            updatedEventData.push(newEvent);
                        }
                    });
                    setEventData(updatedEventData);
                }
            })
            .catch((err: unknown) => {
                console.error('Error fetching event data', err);
            });
    }, [api, auth.user?.profile.sub, setEventData]);

    useEffect(() => {
        fetchEventData();
    }, [queryEventData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll for event list updates every 5 minutes
    useEffect(() => {
        const interval = setInterval(() => {
            fetchEventData();
        }, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchEventData]);

    const clearSelectedEvent = useCallback(() => {
        setViewEvent(null);
        setViewEventData({
            scores: [],
            registrations: [],
            courseData: [],
        });
        setRoundValue(1);
        originalViewEventData.current = { scores: [], registrations: [], courseData: [] };
        originalViewEvent.current = null;
        originalRegistrations.current = [];
    }, [setViewEvent, setViewEventData, setRoundValue, originalViewEventData, originalViewEvent, originalRegistrations]);

    const handleSelectEvent = useCallback(async (event: TbEventWithId | null) => {
        if (!event) {
            clearSelectedEvent();
            return;
        }

        const viewEventDataPassed: ViewEventData = {
            registrations: [],
            scores: [],
            courseData: [],
        };
        let otherCourseIDs: string[] = [];

        const selectedEvent = eventDataRef.current.find((refEvent: TbEventWithId) => refEvent._id === event._id);
        if (!selectedEvent) return;

        const dataDate = Object.keys(selectedEvent).length > 20
            ? new Date(new Date(selectedEvent.modifiedAt).getTime() + 60 * 1000)
            : null;

        await api.get(`/tb/GetEventDetails`, {
            params: {
                _id: selectedEvent._id,
                referenceDate: dataDate,
            },
        })
            .then((res: { data: TbEventWithId | null }) => {
                const eventDataRes = res.data;
                const updatedEventData = [...eventDataRef.current];

                if (eventDataRes) {
                    const existingEventIndex = updatedEventData.findIndex((e) => e._id === eventDataRes._id);
                    if (existingEventIndex !== -1) {
                        updatedEventData[existingEventIndex] = { ...updatedEventData[existingEventIndex], ...eventDataRes };
                    } else {
                        updatedEventData.push(eventDataRes);
                    }

                    const eventToView = updatedEventData.find((e: TbEventWithId) => e._id === eventDataRes._id);
                    if (eventToView) {
                        const normalizedEvent = normalizeEventRounds(eventToView) ?? eventToView;
                        const eventIndex = updatedEventData.findIndex((e: TbEventWithId) => e._id === eventToView._id);
                        if (eventIndex !== -1) updatedEventData[eventIndex] = normalizedEvent;
                        otherCourseIDs = (normalizedEvent.rounds ?? [])
                            .filter((round) => round.courseID !== normalizedEvent.courseID && round.courseID !== null)
                            .map((round) => round.courseID as string);
                        setEventData(updatedEventData);
                        setViewEvent(normalizedEvent);
                        setRoundValue(1);
                        originalViewEvent.current = JSON.parse(JSON.stringify(normalizedEvent));
                    }
                } else if (!eventDataRes && Object.keys(selectedEvent).length > 20) {
                    const normalizedEvent = normalizeEventRounds(selectedEvent) ?? selectedEvent;
                    otherCourseIDs = (normalizedEvent.rounds ?? [])
                        .filter((round) => round.courseID !== normalizedEvent.courseID && round.courseID !== null)
                        .map((round) => round.courseID as string);

                    setViewEvent(normalizedEvent);
                    setRoundValue(1);
                    originalViewEvent.current = JSON.parse(JSON.stringify(normalizedEvent));
                }
            });

        await api.get(`/tb/GetEventRegistrations`, {
            params: { _id: event._id },
        })
            .then((res: { data: TbRegistration[] }) => {
                viewEventDataPassed.registrations = res.data;
            })
            .catch((err: unknown) => {
                console.log('Error fetching registration data', err);
            });

        const courseIDs = [originalViewEvent.current?.courseID, ...otherCourseIDs].filter(Boolean) as string[];
        await api.get(`/tb/GetCourseTeesets`, {
            params: { _ids: courseIDs },
        })
            .then((res: { data: CourseTeeSetData[] }) => {
                viewEventDataPassed.courseData = res.data;
            })
            .catch((err: unknown) => {
                console.log('Error fetching teesets', err);
            });

        await api.get(`/tb/GetEventScores`, {
            params: {
                _id: event._id,
                referenceDate: new Date(Date.UTC(2000, 0, 1)),
            },
        })
            .then((res: { data: EventScoreWithId[] }) => {
                viewEventDataPassed.scores = res.data;
            })
            .catch((err: unknown) => {
                console.log('Error fetching scores', err);
            });

        setViewEventData(viewEventDataPassed);
        setRoundValue(1);
        originalRegistrations.current = JSON.parse(JSON.stringify(viewEventDataPassed.registrations));
        originalViewEventData.current = JSON.parse(JSON.stringify(viewEventDataPassed));
    }, [
        api,
        clearSelectedEvent,
        setEventData,
        setViewEvent,
        setViewEventData,
        setRoundValue,
        originalViewEvent,
        originalRegistrations,
        originalViewEventData,
    ]);

    return {
        fetchEventData,
        handleSelectEvent,
        clearSelectedEvent,
        queryEventData,
        setQueryEventData,
    };
}
